import type { AnyBlob, BlobSyncType, BlobType } from "@pdfking/blob"
import { BytesQ, StringQ } from "@pdfking/structs"
import { Identity } from "@pdfking/tfunc"

/** Anything that has bytes, or will hand them over when asked.
 *
 * Strings and raw buffers are named because they are bytes already; the rest
 * is named by what it can do rather than by what it is — a Blob, a Request, a
 * Response and every blob shape this package defines all answer the same one
 * call, so listing them would only be a way to stop accepting one nobody has
 * heard of yet. A stream is here too, and so is a sequence of any of it: parts
 * are the same thing one level in, which is why the type refers to itself. */
type StreamPart =
  | null
  | undefined
  | AnyBlob
  | ArrayBuffer
  | ReadableStream
  | string
  | Uint8Array
  | BlobType
  | { blob: () => Promise<Blob> }

// AsyncIterable and Iterable spelled out rather than AsyncSequence, which is
// the same two: a union alias is expanded where it is written, so one naming
// the type being declared is circular. An interface reference is not.
export type StreamInput = StreamPart | AsyncIterable<StreamInput> | Iterable<StreamInput>

// A Response and a Request keep their body until asked, and the blob they give
// back holds it without reading it — which is more than can be said for the
// buffer. Named by the call rather than by the two classes that answer it.
const BlobSourceQ = (value: object): value is { blob: () => Promise<Blob> } => "blob" in value

/** What a walk makes of one kind of part. A blob is always a single one — a
 * platform blob or a sync blob — since a BlobArray is walked into its parts. */
type Handler<V, T> = (value: V) => Promise<T> | T

/** A part as the walk finds it: a string, bytes, or a single blob. */
type StreamChunk = string | Uint8Array | Blob | BlobSyncType

/**
 * Whatever a caller has, part by part, as whatever the caller makes of them.
 *
 * The walk everything else is: sequences flattened, a stream drained through a
 * reader — because not every runtime makes a ReadableStream async iterable, and
 * the lock goes back whatever ends the read, a BlobArray taken apart into its
 * parts — and every part handed to the one of three functions that fits it,
 * each passing its parts on as they came unless the caller says otherwise.
 *
 * A blob reaches `blob` as the blob it is, unread: it may be a file nobody
 * wants in memory, and whether that has to change is the caller's call rather
 * than this one's. Nothing is pulled until somebody iterates, and a part is
 * only reached when the iteration gets to it.
 *
 * @param input - A stream, bytes, something blob-shaped, or a sequence of them
 * @param string - What to make of a string part
 * @param bytes - What to make of bytes: a Uint8Array, or an ArrayBuffer viewed
 * @param blob - What to make of a blob, which it is handed without reading
 *
 * @example
 * ```ts
 * import { StreamProcess } from "@pdfking/io"
 *
 * // the size of every part, without reading any of it
 * StreamProcess(input, s => s.length, b => b.length, b => b.size)
 * ```
 */
export async function* StreamProcess<T = StreamChunk>(
  input: StreamInput,
  string = Identity as Handler<string, T>,
  bytes = Identity as Handler<Uint8Array, T>,
  blob = Identity as Handler<BlobType, T>
): AsyncGenerator<T> {
  if (!input) return

  if (StringQ(input)) {
    yield await string(input)
  } else if (BytesQ(input)) {
    yield await bytes(input)
  } else if (input instanceof ArrayBuffer) {
    yield await bytes(new Uint8Array(input))
  } else if ("offsets" in input) {
    for (const part of input.parts) yield* StreamProcess(part, string, bytes, blob)
  } else if ("getReader" in input) {
    const reader = input.getReader()
    let drained = false
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        yield await bytes(value)
      }
      drained = true
    } finally {
      reader.releaseLock()
      // A reader that stopped early leaves the source running: the lock going
      // back is not the same as saying nobody is coming, and whatever the
      // source holds open — a file handle, a socket — waits for the second.
      if (!drained) await input.cancel()
    }
  } else if (BlobSourceQ(input)) {
    yield await blob(await input.blob())
  } else if ("arrayBuffer" in input) {
    yield await blob(input)
  } else {
    for await (const part of input) {
      yield* StreamProcess(part, string, bytes, blob)
    }
  }
}
